
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { showSuccessToast, showErrorToast } from "@/utils/toast-utils";

export function useTestStatusActions(testId?: string) {
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleDeactivateTest = async () => {
    if (!testId) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('tests')
        .update({ is_active: false })
        .eq('id', testId);

      if (error) throw error;
      
      showSuccessToast({
        description: "Test deactivated successfully"
      });
      
      queryClient.invalidateQueries({ queryKey: ['test', testId] });
    } catch (error: any) {
      console.error('Error deactivating test:', error);
      showErrorToast({
        description: error.message || "Failed to deactivate test"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteTest = async () => {
    if (!testId) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('tests')
        .delete()
        .eq('id', testId);

      if (error) throw error;
      
      showSuccessToast({
        description: "Test deleted successfully"
      });
      
      navigate('/test-management');
    } catch (error: any) {
      console.error('Error deleting test:', error);
      showErrorToast({
        description: error.message || "Failed to delete test"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleStatus = async (isActive: boolean) => {
    if (!testId) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('tests')
        .update({ is_active: !isActive })
        .eq('id', testId);

      if (error) throw error;
      
      showSuccessToast({
        description: `Test ${isActive ? 'deactivated' : 'activated'} successfully`
      });
      
      queryClient.invalidateQueries({ queryKey: ['test', testId] });
    } catch (error: any) {
      console.error('Error toggling test status:', error);
      showErrorToast({
        description: error.message || "Failed to update test status"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleVisibility = async (isPublic: boolean) => {
    if (!testId) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('tests')
        .update({ publish_to_job_board: !isPublic })
        .eq('id', testId);

      if (error) throw error;
      
      showSuccessToast({
        description: `Test is now ${isPublic ? 'private' : 'public'}`
      });
      
      queryClient.invalidateQueries({ queryKey: ['test', testId] });
    } catch (error: any) {
      console.error('Error toggling test visibility:', error);
      showErrorToast({
        description: error.message || "Failed to update test visibility"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    handleDeactivateTest,
    handleDeleteTest,
    handleToggleStatus,
    handleToggleVisibility,
    isUpdating
  };
}
